<script lang="ts">
  import { Search, ChevronUp, ChevronDown, X } from 'lucide-svelte'
  import { slide } from 'svelte/transition'
  
  interface Props {
    value: string
    currentMatch: number
    totalMatches: number
    isVisible: boolean
    onSearch: (value: string) => void
    onNext: () => void
    onPrevious: () => void
    onClose: () => void
  }
  
  let {
    value = $bindable(),
    currentMatch = 0,
    totalMatches = 0,
    isVisible = false,
    onSearch,
    onNext,
    onPrevious,
    onClose
  }: Props = $props()
  
  let searchInput = $state<HTMLInputElement | null>(null)
  let internalValue = $state(value || '')
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let isUserTyping = $state(false)
  
  // Initialize internal value when prop changes from parent
  $effect(() => {
    // Sync value from parent when it changes (e.g., from FileSearchView)
    // but not when user is actively typing
    if (value !== undefined && value !== internalValue && !isUserTyping) {
      internalValue = value || ''
    }
  })
  
  // Focus input when search bar becomes visible
  let wasVisible = false
  $effect(() => {
    // Only focus when transitioning from hidden to visible, not on every update
    if (isVisible && searchInput && !wasVisible) {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        // Only focus if the search input isn't already focused
        if (document.activeElement !== searchInput) {
          searchInput?.focus()
        }
        // Don't auto-select to avoid interfering with typing
      })
    }
    wasVisible = isVisible
  })
  
  // Reset when search bar is closed
  $effect(() => {
    if (!isVisible) {
      // Clear internal value when closing
      internalValue = ''
      // Reset typing flag
      isUserTyping = false
      // Clear any pending search
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
        searchDebounceTimer = null
      }
    }
  })
  
  // Cleanup on unmount
  $effect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
      }
    }
  })
  
  function handleInput(event: Event) {
    // Mark that user is typing
    isUserTyping = true
    
    // Get the actual value from the input element
    const target = event.target as HTMLInputElement
    internalValue = target.value
    
    // Clear previous timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
    
    // For first character or clearing search, execute immediately
    // Otherwise use debounce for better performance
    const debounceDelay = (internalValue.length === 1 || internalValue.length === 0) ? 0 : 150
    
    // Set new timer for debounced search
    searchDebounceTimer = setTimeout(() => {
      value = internalValue
      onSearch(internalValue)
      searchDebounceTimer = null
      // User is done typing after debounce
      isUserTyping = false
    }, debounceDelay)
  }
  
  function handleKeyDown(e: KeyboardEvent) {
    // Ignore key events during IME composition (for Korean, Chinese, Japanese input)
    if (e.isComposing || (e as any).keyCode === 229) {
      return
    }
    
    if (e.key === 'Escape') {
      onClose()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      
      // Clear any pending search timer when Enter is pressed
      // This prevents search from being called again after navigation
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
        searchDebounceTimer = null
      }
      
      // Enter navigates between results
      if (totalMatches > 0) {
        if (e.shiftKey) {
          onPrevious()
        } else {
          onNext()
        }
        
        // Keep focus on search input after navigation
        requestAnimationFrame(() => {
          searchInput?.focus()
        })
      }
    }
  }
  
</script>

{#if isVisible}
  <div class="search-bar" transition:slide={{ duration: 200 }}>
    <div class="search-container">
      <Search size={16} class="search-icon" />
      
      <input
        bind:this={searchInput}
        value={internalValue}
        type="text"
        class="search-input"
        placeholder="검색어를 입력하세요..."
        oninput={handleInput}
        onkeydown={handleKeyDown}
      />
      
      {#if totalMatches > 0}
        <span class="match-counter">
          {currentMatch}/{totalMatches}
        </span>
      {/if}
      
      <div class="search-controls">
        <button
          class="search-btn"
          onclick={onPrevious}
          disabled={totalMatches === 0}
          title="이전 찾기 (Shift+Enter)"
        >
          <ChevronUp size={16} />
        </button>
        
        <button
          class="search-btn"
          onclick={onNext}
          disabled={totalMatches === 0}
          title="다음 찾기 (Enter)"
        >
          <ChevronDown size={16} />
        </button>
        
        <div class="divider"></div>
        
        <button
          class="search-btn close-btn"
          onclick={onClose}
          title="닫기 (Esc)"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .search-bar {
    position: relative;
    width: 100%;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-primary);
    padding: 8px max(32px, calc(50% - 500px));
    box-sizing: border-box;
    z-index: 100;
  }
  
  .search-container {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 1000px;
    margin: 0 auto;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    padding: 4px 8px;
    height: 32px;
  }
  
  :global(.search-icon) {
    color: var(--text-secondary);
    flex-shrink: 0;
  }
  
  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 14px;
    color: var(--text-primary);
    font-family: inherit;
  }
  
  .search-input::placeholder {
    color: var(--text-tertiary);
  }
  
  .match-counter {
    font-size: 12px;
    color: var(--text-secondary);
    padding: 0 8px;
    white-space: nowrap;
  }
  
  .search-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .search-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .search-btn:hover:not(:disabled) {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .search-btn:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  .search-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .divider {
    width: 1px;
    height: 16px;
    background: var(--border-primary);
    margin: 0 4px;
  }
  
  .close-btn:hover {
    background: var(--error-bg);
    color: var(--error);
  }
  
  /* Dark mode */
  :global(.dark) .search-container {
    background: var(--bg-secondary);
  }
</style>